import {isBrowser} from '@github-ui/brand-core/utils/platform'
import {useCallback, useEffect, useRef, useState} from 'react'

// eslint-disable-next-line import/no-namespace
import * as Data from './../../data'
import ProductAnnouncementBanner from './../ProductAnnouncementBanner/ProductAnnouncementBanner'
import ProductAnnouncementModal from './../ProductAnnouncementModal/ProductAnnouncementModal'
import {COPY} from './ProductAnnouncement.data'
import type {
  ProductAnnouncementBannerEvent,
  ProductAnnouncementData,
  ProductAnnouncementModalEvent,
} from './ProductAnnouncement.types'

export interface ProductAnnouncementProps {
  id?: string
  announcementId: keyof typeof Data
  label?: string
  shouldShowTabCtas?: boolean
  shouldShowMainCta?: boolean
  useLocalStorage?: boolean
  /**
   * Optional override for the modal's max width on tablet/desktop viewports.
   * Accepts a number (interpreted as pixels) or any valid CSS length.
   */
  modalMaxWidth?: number | string
  onClose?: () => void
  onBannerEvent?: (event: ProductAnnouncementBannerEvent, data?: Record<string, unknown>) => void
  onModalEvent?: (event: ProductAnnouncementModalEvent, data?: Record<string, unknown>) => void
}

const defaultProps: Partial<ProductAnnouncementProps> = {}

const ProductAnnouncement = (props: ProductAnnouncementProps) => {
  const initializedProps: ProductAnnouncementProps = {...defaultProps, ...props}
  const {
    id,
    announcementId,
    label,
    shouldShowTabCtas,
    shouldShowMainCta,
    useLocalStorage,
    modalMaxWidth,
    onClose: onCloseProp,
    onBannerEvent,
    onModalEvent,
  } = initializedProps

  const [isInitialized, setIsInitialized] = useState<boolean>(false)
  const [isBannerVisible, setIsBannerVisible] = useState<boolean>(false)
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
  const announcementIdRef = useRef<ProductAnnouncementProps['announcementId']>(announcementId)

  const getLocalStorageKey = useCallback(
    () => `productAnnouncement.${announcementId}.${id || window.location.pathname}:dismissed`,
    [announcementId, id],
  )

  useEffect(() => {
    if (announcementIdRef.current !== announcementId) setIsModalOpen(false)
    announcementIdRef.current = announcementId
  }, [announcementId])

  useEffect(() => {
    if (isBrowser() && useLocalStorage && !isBannerVisible && isInitialized) {
      localStorage.setItem(getLocalStorageKey(), 'true')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBannerVisible, useLocalStorage])

  useEffect(() => {
    if (isBrowser()) {
      const isDismissed = useLocalStorage && localStorage.getItem(getLocalStorageKey()) === 'true'

      const announcementRawData = Data[announcementId]
      const announcementData = typeof announcementRawData === 'function' ? announcementRawData() : announcementRawData
      const announcementHasBanner = announcementData && 'banner' in announcementData

      if (!isDismissed) {
        if (announcementHasBanner) {
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setIsBannerVisible(true)
        } else {
          setIsModalOpen(true)
        }
      }

      setIsInitialized(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const rawData = Data[announcementId]
  if (!rawData) return null
  const data: ProductAnnouncementData = typeof rawData === 'function' ? rawData() : rawData
  if (!data) return null

  const hasBanner = 'banner' in data

  return (
    <>
      {hasBanner && (
        <ProductAnnouncementBanner
          {...data.banner}
          isVisible={isBannerVisible}
          onClick={() => setIsModalOpen(true)}
          onDismiss={() => setIsBannerVisible(false)}
          aria={COPY.aria.banner}
          announcementId={announcementId}
          onEvent={onBannerEvent}
        />
      )}

      <ProductAnnouncementModal
        {...data.modal}
        label={label || data.modal.label}
        isOpen={isModalOpen}
        maxWidth={modalMaxWidth}
        onClose={() => {
          setIsModalOpen(false)
          if (!hasBanner && useLocalStorage) {
            localStorage.setItem(getLocalStorageKey(), 'true')
          }
          onCloseProp?.()
        }}
        aria={COPY.aria.modal}
        shouldShowTabCtas={shouldShowTabCtas}
        shouldShowMainCta={shouldShowMainCta}
        onEvent={onModalEvent}
      />
    </>
  )
}

export default ProductAnnouncement
